import intl from 'react-intl-universal';
import { ChangeEvent, useRef, useCallback, useEffect } from 'react';
import { FormItem, TextArea, Typography } from '@getsynapse/design-system';

type GoalTitleProps = {
  value?: string;
  isRequired?: boolean;
  onChange: (value: string, prop: string) => void;
};

const MIN_HEIGHT_REM = 2.625;

const GoalTitle = ({ onChange, value, isRequired = true }: GoalTitleProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleAutoResize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';

    const rootFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    const remHeight = textarea.scrollHeight / rootFontSize;
    const newHeight =
      remHeight > MIN_HEIGHT_REM ? `${remHeight}rem` : `${MIN_HEIGHT_REM}rem`;

    textarea.style.height = newHeight;
  }, []);

  useEffect(() => {
    if (isRequired) {
      handleAutoResize();
    }
  }, [handleAutoResize, isRequired]);

  if (!isRequired) {
    return (
      <FormItem
        label={intl.get('STRATEGY_GOALS.ADD_GOAL_MODAL.GOAL_TITLE')}
        labelProps={{
          'data-cy': 'goal-title_label',
        }}
        className='mb-4'
      >
        <Typography variant='body' data-cy='goal-title_value'>
          {value}
        </Typography>
      </FormItem>
    );
  }
  return (
    <TextArea
      label={intl.get('STRATEGY_GOALS.ADD_GOAL_MODAL.GOAL_TITLE')}
      ref={textareaRef}
      value={value}
      textAreaProps={{
        placeholder: intl.get(
          'STRATEGY_GOALS.ADD_GOAL_MODAL.GOAL_TITLE_PLACEHOLDER'
        ),
        rows: 1,
        className: 'overflow-hidden placeholder-neutral',
        'data-testid': 'add_goal_title_input',
        style: { height: '2.625rem' },
      }}
      name='add_goal_title_input'
      onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
        handleAutoResize();
        onChange(event.target.value, 'title');
      }}
      showCharacterCount
      characterCount={255}
      labelProps={{
        required: isRequired,
        'data-cy': 'goal-title_label',
      }}
      className='mb-4'
    />
  );
};

export default GoalTitle;
